import { isTimePermissionEnabled } from '../../config/applicationFeatures';
import React, { useCallback, useMemo, useState } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import {
  Alert,
  Platform,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppCard } from '../../components/AppCard';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ProfileAvatar } from '../../components/ProfileAvatar';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import type { ColorPalette } from '../../constants/colors';
import { darkColors } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import {
  activateEmployee,
  deactivateEmployee,
  getEmployeeCurrentShift,
  getEmployeeDetails,
  getEmployeeLeavePolicies,
  getEmployeeWfhEligibility,
} from '../../services/employeeService';
import { getFaceRegistration } from '../../services/employeeFaceRegistrationService';
import type {
  EmployeeCurrentShift,
  EmployeeDetails,
  EmployeeLeavePolicies,
  EmployeeWfhEligibility,
} from '../../types/employee';
import type { EmployeeAzureRegistration } from '../../types/faceRegistration';
import { faceRegistrationErrorMessage } from '../../utils/faceRegistrationErrors';
import { normalizeApiError } from '../../api/apiError';
import { confirmAction } from '../../utils/confirmAction';
import { formatAttendanceDate } from '../../utils/dateTime';
import { notifyEmployeesChanged } from '../../utils/employeeListSync';
import { runOperation, showOperationFeedback } from '../../utils/operationFeedback';

type Props = NativeStackScreenProps<RootStackParamList, 'EmployeeDetails'>;

type ProfileTab = 'Profile' | 'Work Info' | 'Documents';

const TABS: readonly ProfileTab[] = ['Profile', 'Work Info', 'Documents'];

function displayValue(value: string | null | undefined) {
  return value?.trim() ? value : 'Not available';
}

function yesNo(value: boolean | null | undefined) {
  if (value === null || value === undefined) return 'Not available';
  return value ? 'Yes' : 'No';
}

function InfoRow({
  label,
  value,
  palette,
  last = false,
  onPress,
}: {
  label: string;
  value: string | null | undefined;
  palette: ColorPalette;
  last?: boolean;
  onPress?: () => void;
}) {
  const labelText = `${label}: ${displayValue(value)}`;
  const rowStyle = [
    styles.infoRow,
    !last && {
      borderBottomColor: palette.border,
      borderBottomWidth: StyleSheet.hairlineWidth,
    },
  ];
  const body = (
    <>
      <Text style={[styles.infoLabel, { color: palette.textSecondary }]}>
        {label}
      </Text>
      <Text style={[styles.infoValue, { color: palette.textPrimary }]}>
        {displayValue(value)}
      </Text>
      {onPress ? (
        <Text
          style={[styles.infoChevron, { color: palette.textMuted }]}
          accessibilityElementsHidden
        >
          ›
        </Text>
      ) : null}
    </>
  );

  if (onPress) {
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={labelText}
        onPress={onPress}
        style={({ pressed }) => [rowStyle, { opacity: pressed ? 0.72 : 1 }]}
      >
        {body}
      </Pressable>
    );
  }

  return (
    <View accessibilityLabel={labelText} style={rowStyle}>
      {body}
    </View>
  );
}

function ActiveBadge({
  label,
  palette,
}: {
  label: string;
  palette: ColorPalette;
}) {
  const isDark = palette.background === darkColors.background;
  const normalized = label.trim().toLowerCase();
  const active =
    normalized === 'active' ||
    normalized === 'yes' ||
    normalized.includes('active');
  const inactive =
    normalized === 'inactive' ||
    normalized === 'no' ||
    normalized.includes('inactive') ||
    normalized.includes('deactiv');

  const tone = active
    ? {
        backgroundColor: isDark ? '#054F31' : '#E8F5E9',
        color: isDark ? '#6CE9A6' : '#1B7A3D',
      }
    : inactive
      ? {
          backgroundColor: isDark ? '#7A271A' : '#FFEBEE',
          color: isDark ? '#FDA29B' : '#B42318',
        }
      : {
          backgroundColor: palette.border,
          color: palette.textPrimary,
        };

  return (
    <View
      accessibilityRole="text"
      accessibilityLabel={`Status ${label}`}
      style={[styles.statusBadge, { backgroundColor: tone.backgroundColor }]}
    >
      <Text style={[styles.statusBadgeText, { color: tone.color }]}>{label}</Text>
    </View>
  );
}

function ActionIcon({
  kind,
  color,
}: {
  kind: 'edit' | 'face' | 'password' | 'deactivate' | 'activate';
  color: string;
}) {
  if (kind === 'edit') {
    return (
      <View style={styles.iconBox} accessibilityElementsHidden>
        <View style={[styles.pencilBody, { borderColor: color }]} />
        <View style={[styles.pencilTip, { borderTopColor: color }]} />
      </View>
    );
  }

  if (kind === 'face') {
    return (
      <View style={styles.iconBox} accessibilityElementsHidden>
        <View style={[styles.faceRing, { borderColor: color }]}>
          <View style={styles.faceEyes}>
            <View style={[styles.faceEye, { backgroundColor: color }]} />
            <View style={[styles.faceEye, { backgroundColor: color }]} />
          </View>
          <View style={[styles.faceSmile, { borderColor: color }]} />
        </View>
      </View>
    );
  }

  if (kind === 'password') {
    return (
      <View style={styles.iconBox} accessibilityElementsHidden>
        <View style={[styles.shield, { borderColor: color }]}>
          <View style={[styles.keyhole, { backgroundColor: color }]} />
        </View>
      </View>
    );
  }

  if (kind === 'activate') {
    return (
      <View style={styles.iconBox} accessibilityElementsHidden>
        <View style={[styles.personHead, { borderColor: color }]} />
        <View style={[styles.personBody, { borderColor: color }]} />
        <View style={[styles.plusBadgeHorizontal, { backgroundColor: color }]} />
        <View style={[styles.plusBadgeVertical, { backgroundColor: color }]} />
      </View>
    );
  }

  return (
    <View style={styles.iconBox} accessibilityElementsHidden>
      <View style={[styles.personHead, { borderColor: color }]} />
      <View style={[styles.personBody, { borderColor: color }]} />
      <View style={[styles.minusBadge, { backgroundColor: color }]} />
    </View>
  );
}

function QuickAction({
  title,
  kind,
  onPress,
  palette,
  disabled = false,
  accessibilityLabel,
}: {
  title: string;
  kind: 'edit' | 'face' | 'password' | 'deactivate' | 'activate';
  onPress: () => void;
  palette: ColorPalette;
  disabled?: boolean;
  accessibilityLabel?: string;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? title}
      accessibilityState={{ disabled }}
      disabled={disabled}
      onPress={onPress}
      style={({ pressed }) => [
        styles.quickAction,
        {
          backgroundColor: palette.surface,
          borderColor: palette.border,
          opacity: disabled ? 0.45 : pressed ? 0.82 : 1,
          transform: [{ scale: pressed && !disabled ? 0.97 : 1 }],
        },
      ]}
    >
      <ActionIcon kind={kind} color={palette.primary} />
      <Text style={[styles.quickActionLabel, { color: palette.primary }]}>
        {title}
      </Text>
    </Pressable>
  );
}

export function EmployeeDetailsScreen({ navigation, route }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const employeeId = route.params.employeeId;
  const [employee, setEmployee] = useState<EmployeeDetails | null>(null);
  const [currentShift, setCurrentShift] = useState<EmployeeCurrentShift | null>(
    null,
  );
  const [registration, setRegistration] = useState<EmployeeAzureRegistration | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [faceError, setFaceError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ProfileTab>('Profile');
  const [wfh, setWfh] = useState<EmployeeWfhEligibility | null>(null);
  const [leavePolicies, setLeavePolicies] =
    useState<EmployeeLeavePolicies | null>(null);

  const canViewFace = auth.hasPermission(PERMISSIONS.FACE_REGISTER);
  const canManageFace = canViewFace;
  const canEdit = auth.hasPermission(PERMISSIONS.EMPLOYEE_UPDATE);
  const canActivate = canEdit;
  const canDeactivate = auth.hasPermission(PERMISSIONS.EMPLOYEE_DELETE);
  const canViewAttendance = auth.hasPermission(PERMISSIONS.ATTENDANCE_VIEW);
  const canViewTimeOffBalances = auth.roles?.includes('HR_ADMIN') === true;
  const isInactive = employee?.isActive === false;

  const statusLabel = useMemo(() => {
    if (!employee) return 'Unknown';
    if (employee.status?.trim()) return employee.status.trim();
    if (employee.isActive === true) return 'Active';
    if (employee.isActive === false) return 'Inactive';
    return 'Unknown';
  }, [employee]);

  const load = useCallback(
    async (isRefresh = false) => {
      isRefresh ? setRefreshing(true) : setLoading(true);
      setError(null);
      try {
        const [details, shift, wfhInfo, leaveInfo] = await Promise.all([
          getEmployeeDetails(employeeId),
          getEmployeeCurrentShift(employeeId).catch(() => null),
          canViewTimeOffBalances
            ? getEmployeeWfhEligibility(employeeId).catch(() => null)
            : Promise.resolve(null),
          canViewTimeOffBalances
            ? getEmployeeLeavePolicies(employeeId).catch(() => null)
            : Promise.resolve(null),
        ]);
        setEmployee(details);
        setCurrentShift(shift);
        setWfh(wfhInfo);
        setLeavePolicies(leaveInfo);
      } catch (caught) {
        setError(
          faceRegistrationErrorMessage(
            normalizeApiError(caught).code,
            'Unable to load employee details.',
          ),
        );
        setLoading(false);
        setRefreshing(false);
        return;
      }

      if (canViewFace) {
        try {
          const face = await getFaceRegistration(employeeId);
          setRegistration(face.registration);
          setFaceError(null);
        } catch (caught) {
          setFaceError(
            faceRegistrationErrorMessage(
              normalizeApiError(caught).code,
              'Unable to load face registration.',
            ),
          );
        }
      } else {
        setRegistration(null);
        setFaceError(null);
      }

      setLoading(false);
      setRefreshing(false);
    },
    [canViewFace, canViewTimeOffBalances, employeeId],
  );

  useFocusEffect(
    useCallback(() => {
      load().catch(() => undefined);
    }, [load]),
  );

  const showUnavailable = useCallback((title: string) => {
    Alert.alert(title, 'This action is not available yet.');
  }, []);

  const openAssignShift = useCallback(() => {
    navigation.navigate(routes.employeeAssignShift, { employeeId });
  }, [employeeId, navigation]);

  const openEmployeeEdit = useCallback(() => {
    navigation.navigate(routes.employeeEdit, { employeeId });
  }, [employeeId, navigation]);

  const confirmDeactivate = useCallback(() => {
    if (!employee) return;
    const today = new Date().toISOString().slice(0, 10);
    const message = `Deactivate ${employee.displayName} with last working date ${today}?`;

    if (Platform.OS === 'web') {
      // React Native Web implements Alert.alert as a no-op, so the mobile
      // confirm never runs and the deactivate request is never sent.
      if (employee.isActive === false) {
        showOperationFeedback(
          'update',
          'failure',
          'This employee is already inactive.',
        );
        return;
      }
      confirmAction({
        title: 'Deactivate employee',
        message,
        confirmText: 'Deactivate',
        destructive: true,
        onConfirm: () => {
          runOperation('update', () => deactivateEmployee(employeeId, today), {
            successDetail: result => result.message,
          })
            .then(() => {
              notifyEmployeesChanged();
              return load(true);
            })
            .catch(() => undefined);
        },
      });
      return;
    }

    Alert.alert('Deactivate employee', message, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Deactivate',
        style: 'destructive',
        onPress: () => {
          runOperation('update', () => deactivateEmployee(employeeId, today))
            .then(() => load(true))
            .catch(() => undefined);
        },
      },
    ]);
  }, [employee, employeeId, load]);

  const confirmActivate = useCallback(() => {
    if (!employee) return;
    const message = `Activate ${employee.displayName}?`;

    if (Platform.OS === 'web') {
      // React Native Web implements Alert.alert as a no-op, so the mobile
      // confirm never runs and the activate request is never sent.
      if (employee.isActive === true) {
        showOperationFeedback(
          'update',
          'failure',
          'This employee is already active.',
        );
        return;
      }
      confirmAction({
        title: 'Activate employee',
        message,
        confirmText: 'Activate',
        onConfirm: () => {
          runOperation('update', () => activateEmployee(employeeId), {
            successDetail: result => result.message,
          })
            .then(() => {
              notifyEmployeesChanged();
              return load(true);
            })
            .catch(() => undefined);
        },
      });
      return;
    }

    Alert.alert('Activate employee', message, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Activate',
        onPress: () => {
          runOperation('update', () => activateEmployee(employeeId))
            .then(() => load(true))
            .catch(() => undefined);
        },
      },
    ]);
  }, [employee, employeeId, load]);

  const themed = useMemo(
    () => ({
      sectionTitle: {
        ...typography.sectionTitle,
        color: palette.primary,
        fontWeight: '700' as const,
      },
      errorText: { ...typography.body, color: palette.error },
      muted: { ...typography.body, color: palette.textMuted },
    }),
    [palette],
  );

  return (
    <PermissionGuard
      permission={PERMISSIONS.EMPLOYEE_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      {loading && !employee ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Employee Profile"
            onBack={navigation.goBack}
          />
          <AppLoader message="Loading employee" />
        </AppScreen>
      ) : error && !employee ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Employee Profile"
            onBack={navigation.goBack}
          />
          <ErrorState message={error} onRetry={() => load()} />
        </AppScreen>
      ) : employee ? (
        <AppScreen
          scroll
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => load(true)}
              tintColor={palette.primary}
            />
          }
        >
          <ScreenHeader
            title="Employee Profile"
            onBack={navigation.goBack}
          />

          <View style={styles.body}>

            <View style={styles.profileSummary}>
              <ProfileAvatar name={employee.displayName} size={72} />
              <View style={styles.profileMeta}>
                <View style={styles.profileMetaTop}>
                  <Text
                    style={[styles.employeeNumber, { color: palette.textMuted }]}
                  >
                    {employee.employeeNumber}
                  </Text>
                  <ActiveBadge label={statusLabel} palette={palette} />
                </View>
                <Text
                  style={[styles.displayName, { color: palette.primary }]}
                  numberOfLines={2}
                >
                  {employee.displayName}
                </Text>
                <Text
                  style={[styles.designation, { color: palette.textSecondary }]}
                  numberOfLines={2}
                >
                  {displayValue(employee.designation)}
                </Text>
                {currentShift?.shiftHours ? (
                  <Pressable
                    accessibilityRole={canEdit ? 'button' : 'text'}
                    accessibilityLabel={`Assigned shift hours: ${currentShift.shiftHours}`}
                    disabled={!canEdit}
                    onPress={canEdit ? openAssignShift : undefined}
                  >
                    <Text
                      style={[styles.shiftHours, { color: palette.textMuted }]}
                      numberOfLines={1}
                    >
                      {currentShift.shiftHours}
                    </Text>
                  </Pressable>
                ) : canEdit ? (
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel="Assign shift hours"
                    onPress={openAssignShift}
                  >
                    <Text
                      style={[styles.shiftHours, { color: palette.primary }]}
                      numberOfLines={1}
                    >
                      Assign shift
                    </Text>
                  </Pressable>
                ) : null}
              </View>
            </View>

            <View
              style={[styles.tabs, { borderBottomColor: palette.border }]}
              accessibilityRole="tablist"
            >
              {TABS.map(tab => {
                const selected = tab === activeTab;
                return (
                  <Pressable
                    key={tab}
                    accessibilityRole="tab"
                    accessibilityState={{ selected }}
                    accessibilityLabel={tab}
                    onPress={() => setActiveTab(tab)}
                    style={styles.tab}
                  >
                    <Text
                      style={[
                        styles.tabLabel,
                        {
                          color: selected
                            ? palette.primary
                            : palette.textMuted,
                          fontWeight: selected ? '700' : '500',
                        },
                      ]}
                    >
                      {tab}
                    </Text>
                    <View
                      style={[
                        styles.tabUnderline,
                        {
                          backgroundColor: selected
                            ? palette.primary
                            : palette.transparent,
                        },
                      ]}
                    />
                  </Pressable>
                );
              })}
            </View>

            {activeTab === 'Profile' ? (
              <View style={styles.section}>
                <Text style={themed.sectionTitle}>Personal Information</Text>
                <AppCard style={styles.infoCard}>
                  <InfoRow
                    label="Email"
                    value={employee.officialEmail}
                    palette={palette}
                  />
                  <InfoRow
                    label="Personal email"
                    value={employee.personalEmail}
                    palette={palette}
                  />
                  <InfoRow
                    label="Phone"
                    value={employee.mobileNumber}
                    palette={palette}
                  />
                  <InfoRow
                    label="Status"
                    value={statusLabel}
                    palette={palette}
                    last
                  />
                </AppCard>
              </View>
            ) : null}

            {activeTab === 'Work Info' ? (
              <View style={styles.section}>
                <Text style={themed.sectionTitle}>Employment</Text>
                <AppCard style={styles.infoCard}>
                  <InfoRow
                    label="Employment type"
                    value={employee.employmentType}
                    palette={palette}
                  />
                  {(employee.roles ?? []).length > 0 ? (
                    <InfoRow
                      label="Roles"
                      value={(employee.roles ?? [])
                        .map(role => role.roleName)
                        .join(', ')}
                      palette={palette}
                    />
                  ) : null}
                  <InfoRow
                    label="Joining date"
                    value={
                      employee.joiningDate
                        ? formatAttendanceDate(employee.joiningDate)
                        : null
                    }
                    palette={palette}
                  />
                  <InfoRow
                    label="Department"
                    value={employee.department}
                    palette={palette}
                  />
                  <InfoRow
                    label="Designation"
                    value={employee.designation}
                    palette={palette}
                  />
                  <InfoRow
                    label="Primary location"
                    value={employee.primaryLocation}
                    palette={palette}
                  />
                  <InfoRow
                    label="Reporting manager"
                    value={employee.reportingManager}
                    palette={palette}
                  />
                  <InfoRow
                    label="Shift"
                    value={currentShift?.shiftName}
                    palette={palette}
                    onPress={canEdit ? openAssignShift : undefined}
                  />
                  <InfoRow
                    label="Shift time"
                    value={currentShift?.shiftHours}
                    palette={palette}
                    onPress={canEdit ? openAssignShift : undefined}
                    last={!canEdit}
                  />
                  {canEdit ? (
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel="Update shift time"
                      onPress={openAssignShift}
                      style={({ pressed }) => [
                        styles.infoRow,
                        { opacity: pressed ? 0.72 : 1 },
                      ]}
                    >
                      <Text
                        style={[
                          styles.menuLabel,
                          { color: palette.primary },
                        ]}
                      >
                        Update shift time
                      </Text>
                      <Text
                        style={[
                          styles.infoChevron,
                          { color: palette.primary },
                        ]}
                      >
                        ›
                      </Text>
                    </Pressable>
                  ) : null}
                </AppCard>

                <Text style={[themed.sectionTitle, styles.sectionSpacer]}>
                  Attendance settings
                </Text>
                <AppCard style={styles.infoCard}>
                  <InfoRow
                    label="Attendance required"
                    value={yesNo(employee.attendanceRequired)}
                    palette={palette}
                  />
                  <InfoRow
                    label="Face attendance"
                    value={yesNo(employee.faceAttendanceEnabled)}
                    palette={palette}
                  />
                  <InfoRow
                    label="WFH eligible"
                    value={yesNo(wfh ? wfh.wfhEligible : employee.wfhEligible)}
                    palette={palette}
                    last={!wfh?.wfhEligible && !leavePolicies}
                  />
                  {wfh?.wfhEligible ? (
                    <InfoRow
                      label="Assigned WFH policy"
                      value={
                        wfh.assignedPolicy
                          ? `${wfh.assignedPolicy.policyName}${
                              wfh.assignedPolicy.isDefault ? ' (Default)' : ''
                            }`
                          : 'No policy assigned'
                      }
                      palette={palette}
                      onPress={canEdit ? openEmployeeEdit : undefined}
                      last={!leavePolicies}
                    />
                  ) : null}
                  {leavePolicies ? (
                    <InfoRow
                      label="Leave policies"
                      value={
                        leavePolicies.assignedPolicies.length
                          ? leavePolicies.assignedPolicies
                              .map(
                                policy =>
                                  `${policy.leaveTypeName}: ${policy.policyName}${
                                    policy.isDefault ? ' (Default)' : ''
                                  }`,
                              )
                              .join('\n')
                          : 'No leave policy assigned'
                      }
                      palette={palette}
                      onPress={canEdit ? openEmployeeEdit : undefined}
                      last
                    />
                  ) : null}
                </AppCard>

                <Text style={[themed.sectionTitle, styles.sectionSpacer]}>
                  Face registration
                </Text>
                <AppCard style={styles.infoCard}>
                  {faceError ? (
                    <View style={styles.inlineError} accessibilityRole="alert">
                      <Text style={themed.errorText}>{faceError}</Text>
                      <Pressable
                        accessibilityRole="button"
                        accessibilityLabel="Retry face registration load"
                        onPress={() => load()}
                      >
                        <Text
                          style={[styles.retryLink, { color: palette.primary }]}
                        >
                          Retry
                        </Text>
                      </Pressable>
                    </View>
                  ) : registration ? (
                    <>
                      <InfoRow
                        label="Configured"
                        value={yesNo(registration.azurePersonConfigured)}
                        palette={palette}
                      />
                      <InfoRow
                        label="Person status"
                        value={registration.personStatus}
                        palette={palette}
                      />
                      <InfoRow
                        label="Sync status"
                        value={registration.syncStatus}
                        palette={palette}
                      />
                      <InfoRow
                        label="Registration"
                        value={registration.registrationStatus}
                        palette={palette}
                      />
                      <InfoRow
                        label="Training"
                        value={registration.trainingStatus}
                        palette={palette}
                      />
                      <InfoRow
                        label="Active samples"
                        value={String(registration.activeFaceCount)}
                        palette={palette}
                      />
                      <InfoRow
                        label="Ready for attendance"
                        value={yesNo(registration.isReady)}
                        palette={palette}
                        last
                      />
                    </>
                  ) : (
                    <Text style={themed.muted}>
                      {canManageFace
                        ? 'No face registration loaded.'
                        : 'Face registration details require FACE_REGISTER permission.'}
                    </Text>
                  )}
                </AppCard>
              </View>
            ) : null}

            {activeTab === 'Documents' ? (
              <View style={styles.section}>
                <Text style={themed.sectionTitle}>Documents</Text>
                <AppCard>
                  <EmptyState
                    title="No documents"
                    message="Employee documents are not available in this app yet."
                  />
                </AppCard>
              </View>
            ) : null}

            {canEdit || canViewAttendance || canViewTimeOffBalances ? (
              <View style={styles.section}>
                <AppCard style={styles.menuCard}>
                  {canEdit ? (
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={
                        currentShift ? 'Update shift' : 'Assign shift'
                      }
                      onPress={openAssignShift}
                      style={({ pressed }) => [
                        styles.menuRow,
                        canViewAttendance
                          ? {
                              borderBottomColor: palette.border,
                              borderBottomWidth: StyleSheet.hairlineWidth,
                            }
                          : null,
                        { opacity: pressed ? 0.72 : 1 },
                      ]}
                    >
                      <Text
                        style={[
                          styles.menuLabel,
                          { color: palette.textPrimary },
                        ]}
                        numberOfLines={1}
                      >
                        {currentShift ? 'Update Shift' : 'Assign Shift'}
                      </Text>
                      <Text
                        style={[
                          styles.menuChevron,
                          { color: palette.textMuted },
                        ]}
                      >
                        ›
                      </Text>
                    </Pressable>
                  ) : null}
                  {canViewAttendance ? (
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel="View attendance history"
                      onPress={() =>
                        navigation.navigate(routes.attendanceHistory, {
                          employeeId,
                        })
                      }
                      style={({ pressed }) => [
                        styles.menuRow,
                        { opacity: pressed ? 0.72 : 1 },
                      ]}
                    >
                      <Text
                        style={[
                          styles.menuLabel,
                          { color: palette.textPrimary },
                        ]}
                        numberOfLines={1}
                      >
                        Attendance History
                      </Text>
                      <Text
                        style={[
                          styles.menuChevron,
                          { color: palette.textMuted },
                        ]}
                      >
                        ›
                      </Text>
                    </Pressable>
                  ) : null}
                  {canViewTimeOffBalances ? (
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={isTimePermissionEnabled() ? "View leave WFH and permission balances" : "View leave and WFH balances"}
                      onPress={() => navigation.navigate(routes.employeeTimeOffBalance, { employeeId })}
                      style={({ pressed }) => [styles.menuRow, { opacity: pressed ? 0.72 : 1 }]}
                    >
                      <Text style={[styles.menuLabel, { color: palette.textPrimary }]} numberOfLines={1}>
                        {isTimePermissionEnabled() ? 'Leave, WFH & Permission Balance' : 'Leave & WFH Balance'}
                      </Text>
                      <Text style={[styles.menuChevron, { color: palette.textMuted }]}>›</Text>
                    </Pressable>
                  ) : null}
                  {canViewTimeOffBalances && auth.hasPermission(PERMISSIONS.SETTINGS_VIEW) ? (
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel="Update employee leave balances"
                      onPress={() => navigation.navigate(routes.leaveBalanceEmployee, { employeeId })}
                      style={({ pressed }) => [styles.menuRow, { opacity: pressed ? 0.72 : 1 }]}
                    >
                      <Text style={[styles.menuLabel, { color: palette.textPrimary }]} numberOfLines={1}>
                        Update Leave Balance
                      </Text>
                      <Text style={[styles.menuChevron, { color: palette.textMuted }]}>›</Text>
                    </Pressable>
                  ) : null}
                </AppCard>
              </View>
            ) : null}

            <View style={styles.section}>
              <Text style={themed.sectionTitle}>Quick Actions</Text>
              <View style={styles.quickActions}>
                <QuickAction
                  title="Edit"
                  kind="edit"
                  palette={palette}
                  disabled={!canEdit}
                  onPress={openEmployeeEdit}
                />
                {canManageFace ? (
                  <QuickAction
                    title={'Face\nRegistration'}
                    kind="face"
                    palette={palette}
                    accessibilityLabel="Manage face registration"
                    onPress={() =>
                      navigation.navigate(routes.employeeFaceRegistration, {
                        employeeId,
                      })
                    }
                  />
                ) : null}
                <QuickAction
                  title={'Reset\nPassword'}
                  kind="password"
                  palette={palette}
                  onPress={() => showUnavailable('Reset Password')}
                />
                {isInactive ? (
                  <QuickAction
                    title="Activate"
                    kind="activate"
                    palette={palette}
                    disabled={!canActivate}
                    onPress={() =>
                      canActivate
                        ? confirmActivate()
                        : showUnavailable('Activate')
                    }
                  />
                ) : (
                  <QuickAction
                    title="Deactivate"
                    kind="deactivate"
                    palette={palette}
                    disabled={!canDeactivate}
                    onPress={() =>
                      canDeactivate
                        ? confirmDeactivate()
                        : showUnavailable('Deactivate')
                    }
                  />
                )}
              </View>
            </View>
          </View>
        </AppScreen>
      ) : null}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  center: { flex: 1, justifyContent: 'center' },
  body: {
    marginTop: spacing.lg,
    gap: spacing.xl,
  },
  profileSummary: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.lg,
  },
  profileMeta: {
    flex: 1,
    gap: spacing.xs,
  },
  profileMetaTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  employeeNumber: {
    ...typography.caption,
    fontWeight: '500',
    letterSpacing: 0.3,
  },
  displayName: {
    ...typography.sectionTitle,
    fontSize: 22,
    lineHeight: 28,
    fontWeight: '700',
  },
  designation: {
    ...typography.body,
    fontSize: 15,
  },
  shiftHours: {
    ...typography.caption,
    fontSize: 13,
    marginTop: 2,
  },
  statusBadge: {
    borderRadius: 999,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
  },
  statusBadgeText: {
    ...typography.caption,
    fontWeight: '700',
  },
  tabs: {
    flexDirection: 'row',
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  tab: {
    flex: 1,
    alignItems: 'center',
    paddingTop: spacing.sm,
  },
  tabLabel: {
    ...typography.body,
    fontSize: 15,
    paddingBottom: spacing.sm,
  },
  tabUnderline: {
    alignSelf: 'stretch',
    height: 3,
    borderTopLeftRadius: 2,
    borderTopRightRadius: 2,
  },
  section: {
    gap: spacing.md,
  },
  sectionSpacer: {
    marginTop: spacing.sm,
  },
  menuCard: {
    paddingVertical: 0,
    paddingHorizontal: spacing.lg,
  },
  menuRow: {
    minHeight: 56,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  menuLabel: {
    ...typography.body,
    flex: 1,
    fontWeight: '500',
  },
  menuChevron: {
    fontSize: 28,
    lineHeight: 28,
    fontWeight: '300',
    textAlign: 'center',
    minWidth: 24,
    marginTop: -2,
  },
  infoCard: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.lg,
    gap: 0,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.lg,
    paddingVertical: spacing.md,
  },
  infoLabel: {
    ...typography.body,
    fontSize: 15,
    flexShrink: 0,
    maxWidth: '42%',
  },
  infoValue: {
    ...typography.body,
    fontSize: 15,
    fontWeight: '500',
    textAlign: 'right',
    flex: 1,
  },
  infoChevron: {
    fontSize: 22,
    lineHeight: 22,
    fontWeight: '300',
    minWidth: 16,
    textAlign: 'center',
    marginTop: 1,
  },
  inlineError: {
    gap: spacing.sm,
    paddingVertical: spacing.md,
  },
  retryLink: {
    ...typography.button,
    fontSize: 15,
  },
  quickActions: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  quickAction: {
    flex: 1,
    minHeight: 88,
    borderWidth: 1,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.xs,
    paddingVertical: spacing.md,
    gap: spacing.sm,
    shadowColor: '#0F172A',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 1,
  },
  quickActionLabel: {
    ...typography.caption,
    fontSize: 11,
    lineHeight: 14,
    fontWeight: '700',
    textAlign: 'center',
  },
  iconBox: {
    width: 28,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pencilBody: {
    width: 14,
    height: 18,
    borderWidth: 2,
    borderRadius: 2,
    transform: [{ rotate: '-20deg' }],
  },
  pencilTip: {
    position: 'absolute',
    bottom: 2,
    width: 0,
    height: 0,
    borderLeftWidth: 4,
    borderRightWidth: 4,
    borderTopWidth: 6,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    transform: [{ rotate: '-20deg' }],
  },
  faceRing: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 3,
  },
  faceEyes: {
    flexDirection: 'row',
    gap: 5,
    marginTop: 2,
  },
  faceEye: {
    width: 3,
    height: 3,
    borderRadius: 1.5,
  },
  faceSmile: {
    width: 10,
    height: 5,
    borderBottomWidth: 2,
    borderLeftWidth: 2,
    borderRightWidth: 2,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    borderColor: 'transparent',
  },
  shield: {
    width: 18,
    height: 22,
    borderWidth: 2,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  keyhole: {
    width: 5,
    height: 8,
    borderRadius: 2.5,
  },
  personHead: {
    width: 10,
    height: 10,
    borderRadius: 5,
    borderWidth: 2,
    marginBottom: 2,
  },
  personBody: {
    width: 18,
    height: 10,
    borderWidth: 2,
    borderTopLeftRadius: 9,
    borderTopRightRadius: 9,
    borderBottomWidth: 0,
  },
  minusBadge: {
    position: 'absolute',
    right: 0,
    bottom: 4,
    width: 10,
    height: 2.5,
    borderRadius: 1,
  },
  plusBadgeHorizontal: {
    position: 'absolute',
    right: 0,
    bottom: 5,
    width: 10,
    height: 2.5,
    borderRadius: 1,
  },
  plusBadgeVertical: {
    position: 'absolute',
    right: 3.75,
    bottom: 1.25,
    width: 2.5,
    height: 10,
    borderRadius: 1,
  },
});
